<script lang="ts">
	import { devices_settings } from 'src/stores/device-runtime/status';
	import { readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Services
	import { format } from 'src/services/i18n';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	//Helpers
	import { real_id } from 'src/helpers/interface_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	//Types
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	//Design system components
	import { Toggle, toast } from '@Shelly/shelly-design-system';
	import SettingGuide from '../../../SettingGuide.svelte';

	export let device: Device;
	// Which input this row controls. Falls back to the device channel so the
	// component behaves like InvertSwitchG2 when no explicit id is provided.
	export let inputId: number = device.channel;
	export let title = '';
	export let deferSave = false;
	export let value: boolean | undefined;

	const id = real_id(device.id);
	const component = `input:${inputId}`;

	let settings = readDeviceSettings(id);

	let invert = settings[component].invert;
	let cache = invert;
	let waitingRorRequest = false;

	$: displayed = deferSave && typeof value === 'boolean' ? value : invert;

	const sentRequests = async () => {
		if (deferSave) {
			value = !displayed;
			return;
		}
		const newValueInvert = !invert;
		if (waitingRorRequest || cache === newValueInvert) return;
		waitingRorRequest = true;
		try {
			const params = { id: inputId, config: { invert: newValueInvert } };

			const res: SettingRequest = await gen2Request(device, 'Input.SetConfig', params);

			if (res.isok) {
				invert = newValueInvert;
				settings[component].invert = invert;
				devices_settings.update_devices_settings(id, settings);
				toast.open({
					type: 'success',
					title: $format('setting_scc_saved_toast', {
						default: 'Setting saved successfully.',
					}),
				});
				cache = invert;
			} else {
				toast.open({
					type: 'danger',
					title: $format('invert_input_err_toast', { default: 'Could not invert input.' }),
				});
				invert = cache;
			}
		} finally {
			waitingRorRequest = false;
		}
	};
</script>

<SettingGuide
	{title}
	text={$format('settings_guide_invert_switch', {
		default:
			'Swap which switch position is on and which is off, so the wall switch matches how it is wired.',
	})}
/>

<Toggle checked={displayed} size="m" on:click={sentRequests} id={`toggle-invert-input-${inputId}`}>
	{capFirstChar(
		$format('settings_toggle_invert', {
			default: 'Invert',
		}),
	)}
</Toggle>
